/**
 * Employee Layout for Desktop Web
 * A simpler layout without sidebar for employee screens
 * Shows only header and main content area
 */
import React from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, Image } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DesktopTheme } from '../theme/desktopTheme';
import { useDesktopDetect } from '../hooks/useDesktopDetect';

// Simple header for employee screens
const EmployeeHeader = ({
  title,
  subtitle,
  userName,
  companyName,
  onLogout,
  showBackButton,
  onBack
}) => {
  return (
    <View style={headerStyles.container}>
      <View style={headerStyles.leftSection}>
        {showBackButton && (
          <TouchableOpacity
            style={headerStyles.backButton}
            onPress={onBack}
          >
            <Ionicons name="arrow-back" size={20} color={DesktopTheme.colors.textPrimary} />
          </TouchableOpacity>
        )}
        <View style={headerStyles.logoContainer}>
          <View style={headerStyles.logoIcon}>
            <Ionicons name="leaf" size={24} color="#fff" />
          </View>
          <Text style={headerStyles.logoText}>ESG Portal</Text>
        </View>
        {title && (
          <View style={headerStyles.titleContainer}>
            <View style={headerStyles.divider} />
            <View>
              <Text style={headerStyles.title}>{title}</Text>
              {subtitle && <Text style={headerStyles.subtitle}>{subtitle}</Text>}
            </View>
          </View>
        )}
      </View>

      <View style={headerStyles.rightSection}>
        {companyName && (
          <View style={headerStyles.companyBadge}>
            <Ionicons name="business-outline" size={16} color={DesktopTheme.colors.primary} />
            <Text style={headerStyles.companyName}>{companyName}</Text>
          </View>
        )}
        {userName && (
          <View style={headerStyles.userInfo}>
            <View style={headerStyles.avatar}>
              <Text style={headerStyles.avatarText}>
                {userName.charAt(0).toUpperCase()}
              </Text>
            </View>
            <Text style={headerStyles.userName}>{userName}</Text>
          </View>
        )}
        {onLogout && (
          <TouchableOpacity
            style={headerStyles.logoutButton}
            onPress={onLogout}
          >
            <Ionicons name="log-out-outline" size={20} color={DesktopTheme.colors.textSecondary} />
            <Text style={headerStyles.logoutText}>Logout</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const EmployeeLayout = ({
  children,
  title,
  subtitle,
  userName,
  companyName,
  onLogout,
  showBackButton = false,
  onBack,
  showHeader = true,
}) => {
  const { isDesktopWeb } = useDesktopDetect();

  // Only apply desktop layout on web with sufficient width
  if (!isDesktopWeb) {
    return <>{children}</>;
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      {showHeader && (
        <EmployeeHeader
          title={title}
          subtitle={subtitle}
          userName={userName}
          companyName={companyName}
          onLogout={onLogout}
          showBackButton={showBackButton}
          onBack={onBack}
        />
      )}

      {/* Content */}
      <View style={styles.content}>{children}</View>
    </View>
  );
};

const { colors, typography, spacing, shadows } = DesktopTheme;

const headerStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.surface,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...shadows.sm,
    ...(Platform.OS === 'web' && {
      position: 'sticky',
      top: 0,
      zIndex: 100,
    }),
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: colors.contentBg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.sm,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  logoIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoText: {
    fontSize: typography.fontSize.h4,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  divider: {
    width: 1,
    height: 32,
    backgroundColor: colors.border,
  },
  title: {
    fontSize: typography.fontSize.h4,
    fontWeight: typography.fontWeight.semibold,
    color: colors.textPrimary,
  },
  subtitle: {
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
    marginTop: 2,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
  },
  companyBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.primaryLight,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 20,
  },
  companyName: {
    fontSize: typography.fontSize.small,
    fontWeight: typography.fontWeight.medium,
    color: colors.primary,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#fff',
    fontSize: typography.fontSize.body,
    fontWeight: typography.fontWeight.semibold,
  },
  userName: {
    fontSize: typography.fontSize.body,
    fontWeight: typography.fontWeight.medium,
    color: colors.textPrimary,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 8,
    backgroundColor: colors.contentBg,
  },
  logoutText: {
    fontSize: typography.fontSize.small,
    color: colors.textSecondary,
  },
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: colors.contentBg,
    ...(Platform.OS === 'web' && {
      height: '100vh',
      overflow: 'hidden',
    }),
  },
  content: {
    flex: 1,
    backgroundColor: colors.contentBg,
    ...(Platform.OS === 'web' && {
      overflow: 'auto',
    }),
  },
});

export default EmployeeLayout;
